export const DEFAULT_ORDER_STATUSES = [
  { label: 'Confirmed', value: 'confirmed', color: '#2563eb', position: 0 },
  { label: 'Shipped', value: 'shipped', color: '#0369a1', position: 1 },
  { label: 'Delivered', value: 'delivered', color: '#16a34a', position: 2 },
];

export const DEFAULT_RETURN_ORDER_STATUSES = [
  { label: 'Return Placed', value: 'return_placed', color: '#7c3aed', position: 0 },
  { label: 'Return Picked Up', value: 'return_picked_up', color: '#a855f7', position: 1 },
  { label: 'Return Reached', value: 'return_reached', color: '#16a34a', position: 2 },
];

export const DEFAULT_RETURN_NEGATIVE_ORDER_STATUSES = [];

export const DEFAULT_REPLACEMENT_ORDER_STATUSES = [
  { label: 'Replacement Placed', value: 'replacement_placed', color: '#0369a1', position: 0 },
  { label: 'Replacement Picked Up', value: 'replacement_picked_up', color: '#0284c7', position: 1 },
  { label: 'Delivered', value: 'delivered', color: '#16a34a', position: 2 },
];

export const DEFAULT_REPLACEMENT_NEGATIVE_ORDER_STATUSES = [
  {
    label: 'Replacement Cancelled',
    value: 'replacement_cancelled',
    color: '#991b1b',
    position: 0,
    availableIn: [],
    subStatuses: [],
  },
];

export const DEFAULT_NEGATIVE_ORDER_STATUSES = [
  {
    label: 'Cancelled',
    value: 'cancelled',
    color: '#991b1b',
    position: 0,
    availableIn: ['pending', 'confirmed', 'shipped', 'transit', 'out_for_delivery', 'delivered'],
    subStatuses: [],
  },
  {
    label: 'Returned',
    value: 'returned',
    color: '#7f1d1d',
    position: 1,
    availableIn: ['shipped', 'transit', 'out_for_delivery', 'delivered'],
    subStatuses: [{ label: 'Return Reached', value: 'return_reached', color: '#5c1212', position: 0 }],
  },
  {
    label: 'Replacement',
    value: 'replacement',
    color: '#991b1b',
    position: 2,
    availableIn: ['delivered'],
    subStatuses: [{ label: 'Replacement Reached', value: 'replacement_reached', color: '#6b1414', position: 0 }],
  },
];

export const SYSTEM_AVAILABLE_STATUSES = [
  { label: 'Pending', value: 'pending' },
];

const DEFAULT_NEGATIVE_AVAILABLE_IN = {
  cancelled: ['pending', 'confirmed', 'shipped', 'transit', 'out_for_delivery', 'delivered'],
  returned: ['shipped', 'transit', 'out_for_delivery', 'delivered'],
  return: ['shipped', 'transit', 'out_for_delivery', 'delivered'],
  replacement: ['delivered'],
};

const DEFAULT_NEGATIVE_SUB_STATUSES = {
  returned: [{ label: 'Return Reached', value: 'return_reached', color: '#5c1212', position: 0 }],
  return: [{ label: 'Return Reached', value: 'return_reached', color: '#5c1212', position: 0 }],
  replacement: [{ label: 'Replacement Reached', value: 'replacement_reached', color: '#6b1414', position: 0 }],
};

const normalizeSubStatuses = (subStatuses, fallback = [], parentColor = '#991b1b') => {
  const source = Array.isArray(subStatuses) ? subStatuses : fallback;
  const fallbackByValue = new Map(
    (Array.isArray(fallback) ? fallback : []).map((item) => [String(item?.value || '').trim().toLowerCase(), item])
  );
  return (Array.isArray(source) ? source : [])
    .map((item, index) => {
      const label = String(item?.label || item?.value || '').trim();
      const value = String(item?.value || slugifyFieldKey(label)).trim().toLowerCase();
      if (!label || !value) return null;
      const fallbackItem = fallbackByValue.get(value);
      const color = String(
        item?.color || fallbackItem?.color || parentColor || '#991b1b'
      ).trim() || parentColor || '#991b1b';
      return {
        label,
        value,
        color,
        position: Number.isFinite(item?.position) ? item.position : index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.position - b.position)
    .map(({ label, value, color }, index) => ({ label, value, color, position: index }));
};

export const normalizeNegativeOrderStatuses = (statuses = []) =>
  (Array.isArray(statuses) ? statuses : [])
    .map((item, index) => {
      const label = String(item?.label || item?.value || '').trim();
      const value = String(item?.value || slugifyFieldKey(label)).trim().toLowerCase();
      if (!label || !value) return null;
      const availableIn = (item?.availableIn !== undefined && item?.availableIn !== null
        ? item.availableIn
        : DEFAULT_NEGATIVE_AVAILABLE_IN[value] || [])
        .map((entry) => normalizeStatusValue(entry))
        .filter(Boolean);
      const parentColor = String(item?.color || '#991b1b').trim() || '#991b1b';
      return {
        label,
        value,
        color: parentColor,
        position: Number.isFinite(item?.position) ? item.position : index,
        availableIn: [...new Set(availableIn)],
        subStatuses: normalizeSubStatuses(
          item?.subStatuses,
          item?.subStatuses === undefined ? (DEFAULT_NEGATIVE_SUB_STATUSES[value] || []) : [],
          parentColor
        ),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.position - b.position)
    .map(({ label, value, color, availableIn, subStatuses }, index) => ({
      label,
      value,
      color,
      availableIn,
      subStatuses,
      position: index,
    }));

export const getAllNegativeStatusValues = (negativeOrderStatuses = []) => {
  const values = [];
  normalizeNegativeOrderStatuses(negativeOrderStatuses).forEach((item) => {
    values.push(item.value);
    (item.subStatuses || []).forEach((sub) => values.push(sub.value));
  });
  return values;
};

export const getMainStatusOptionsForNegative = (orderStatuses = [], negativeOrderStatuses = []) => {
  const positive = getPositiveOrderStatuses(orderStatuses, negativeOrderStatuses);
  const options = [...SYSTEM_AVAILABLE_STATUSES];
  positive.forEach((item) => {
    if (!options.some((option) => option.value === item.value)) {
      options.push({ value: item.value, label: item.label });
    }
  });
  return options;
};

export const PAYMENT_TYPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'cod', label: 'COD' },
  { value: 'advance_paid', label: 'Advance Payed' },
];

export const SHIPMENT_FIELD_KEYS = {
  number: 'shipment_number',
  partner: 'shipment_partner',
};

export const SHIPMENT_PARTNER_OPTIONS = [
  { value: 'delhivery', label: 'Delhivery' },
  { value: 'dtdc', label: 'DTDC' },
  { value: 'indian_post', label: 'Indian Post' },
];

export const formatShipmentPartner = (value) => {
  const match = SHIPMENT_PARTNER_OPTIONS.find((item) => item.value === value);
  return match?.label || String(value || '').trim() || '---';
};

export const isShippedStatus = (status) => normalizeStatusValue(status) === 'shipped';

const STATUS_ALIASES = {
  'on delivery': 'shipped',
  in_progress: 'shipped',
  done: 'delivered',
  // Legacy last step of replacement flow (now normal delivered)
  replacement_reached: 'delivered',
};

export const slugifyFieldKey = (label) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const formatFieldLabel = (label, required = false) => {
  const text = String(label || '').trim();
  if (!text) return '';
  return required ? `${text} *` : `${text} (optional)`;
};

export const normalizeStatusValue = (status) => {
  if (status && typeof status === 'object') {
    return String(status.value || '').trim().toLowerCase();
  }
  const normalized = String(status || '').trim().toLowerCase();
  return STATUS_ALIASES[normalized] || normalized;
};

export const getStatusMeta = (status, orderStatuses = [], negativeOrderStatuses = []) => {
  const normalized = normalizeStatusValue(status);
  const all = [
    ...(orderStatuses || []),
    ...normalizeNegativeOrderStatuses(negativeOrderStatuses),
    ...DEFAULT_RETURN_ORDER_STATUSES,
    ...normalizeNegativeOrderStatuses(DEFAULT_RETURN_NEGATIVE_ORDER_STATUSES),
    ...DEFAULT_REPLACEMENT_ORDER_STATUSES,
    ...normalizeNegativeOrderStatuses(DEFAULT_REPLACEMENT_NEGATIVE_ORDER_STATUSES),
  ];
  const match = all.find((item) => item.value === normalized);
  if (match) return match;

  for (const parent of [
    ...normalizeNegativeOrderStatuses(negativeOrderStatuses),
    ...normalizeNegativeOrderStatuses(DEFAULT_RETURN_NEGATIVE_ORDER_STATUSES),
    ...normalizeNegativeOrderStatuses(DEFAULT_REPLACEMENT_NEGATIVE_ORDER_STATUSES),
  ]) {
    const sub = (parent.subStatuses || []).find((item) => item.value === normalized);
    if (sub) {
      return {
        ...sub,
        color: sub.color || parent.color,
        parentValue: parent.value,
      };
    }
  }

  return {
    label: normalized ? normalized.replace(/\b\w/g, (c) => c.toUpperCase()) : '-',
    value: normalized,
    color: '#2563eb',
  };
};

export const getFlowStatusesForOrder = (order, orderConfig = {}) => {
  const orderType = String(order?.type || 'order').toLowerCase();
  if (orderType === 'return') {
    return {
      orderStatuses: orderConfig.returnOrderStatuses?.length
        ? orderConfig.returnOrderStatuses
        : DEFAULT_RETURN_ORDER_STATUSES,
      negativeOrderStatuses: DEFAULT_RETURN_NEGATIVE_ORDER_STATUSES,
    };
  }
  if (orderType === 'replacement') {
    return {
      orderStatuses: DEFAULT_REPLACEMENT_ORDER_STATUSES,
      negativeOrderStatuses: DEFAULT_REPLACEMENT_NEGATIVE_ORDER_STATUSES,
    };
  }
  return {
    orderStatuses: orderConfig.orderStatuses || order?.orderConfig?.orderStatuses || [],
    negativeOrderStatuses:
      orderConfig.negativeOrderStatuses || order?.orderConfig?.negativeOrderStatuses || [],
  };
};

export const formatOrderTypeLabel = (type) => {
  const value = String(type || 'order').toLowerCase();
  if (value === 'return') return 'Return';
  if (value === 'replacement') return 'Replacement';
  return 'Order';
};

/** Non-null only for return / replacement — used for dispatch badges. */
export const getOrderTypeBadge = (type) => {
  const value = String(type || '').toLowerCase();
  if (value === 'return') return { label: 'Return', kind: 'return' };
  if (value === 'replacement') return { label: 'Replacement', kind: 'replacement' };
  return null;
};

export const getPositiveOrderStatuses = (orderStatuses = [], negativeOrderStatuses = []) => {
  const negativeValues = new Set([
    ...(negativeOrderStatuses || []).map((item) => item.value),
    ...DEFAULT_NEGATIVE_ORDER_STATUSES.map((item) => item.value),
  ]);
  return [...(orderStatuses || [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .filter((item) => !negativeValues.has(item.value));
};

export const isNegativeOrderStatus = (status, negativeOrderStatuses = []) => {
  const normalized = normalizeStatusValue(status);
  return getAllNegativeStatusValues(negativeOrderStatuses).includes(normalized);
};

export const getAvailableNegativeStatuses = (currentStatus, negativeOrderStatuses = []) => {
  const current = normalizeStatusValue(currentStatus);
  return normalizeNegativeOrderStatuses(negativeOrderStatuses).filter((item) =>
    (item.availableIn || []).some((value) => normalizeStatusValue(value) === current)
  );
};

export const getNegativeStatusParent = (currentStatus, negativeOrderStatuses = []) => {
  const current = normalizeStatusValue(currentStatus);
  return normalizeNegativeOrderStatuses(negativeOrderStatuses).find((parent) => {
    if (parent.value === current) return true;
    return (parent.subStatuses || []).some((sub) => sub.value === current);
  }) || null;
};

export const getAvailableNegativeSubStatuses = (currentStatus, negativeOrderStatuses = []) => {
  const current = normalizeStatusValue(currentStatus);
  const parent = getNegativeStatusParent(currentStatus, negativeOrderStatuses);
  if (!parent) return [];

  const subs = [...(parent.subStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  if (parent.value === current) {
    return subs;
  }

  const subIndex = subs.findIndex((item) => item.value === current);
  if (subIndex >= 0) {
    return subs.slice(subIndex + 1);
  }

  return [];
};

export const buildNegativeStatusDropdownLabel = (availableNegativeStatuses = []) => {
  const labels = (availableNegativeStatuses || []).map((item) => item.label).filter(Boolean);
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
};

const CANCEL_NEGATIVE_VALUES = new Set(['cancel', 'cancelled', 'canceled']);
const RETURN_NEGATIVE_VALUES = new Set(['return', 'returned']);

const toNegativeGroupShortLabel = (parent) => {
  const value = normalizeStatusValue(parent?.value);
  if (CANCEL_NEGATIVE_VALUES.has(value)) return 'Cancel';
  if (RETURN_NEGATIVE_VALUES.has(value)) return 'Return';
  return parent?.label || null;
};

export const buildNegativeStatusGroupLabel = (negativeOrderStatuses = []) => {
  const labels = [];
  const seen = new Set();

  normalizeNegativeOrderStatuses(negativeOrderStatuses).forEach((parent) => {
    const shortLabel = toNegativeGroupShortLabel(parent);
    if (!shortLabel || seen.has(shortLabel)) return;
    seen.add(shortLabel);
    labels.push(shortLabel);
  });

  return buildNegativeStatusDropdownLabel(labels.map((label) => ({ label })));
};

export const getNextOrderStatus = (currentStatus, orderStatuses = [], negativeOrderStatuses = []) => {
  const current = normalizeStatusValue(currentStatus);
  const list = getPositiveOrderStatuses(orderStatuses, negativeOrderStatuses);
  const index = list.findIndex((item) => item.value === current);

  if (index >= 0 && index < list.length - 1) {
    return list[index + 1].value;
  }

  if (['pending', 'assigned', ''].includes(current) && list.length > 0) {
    return list[0].value;
  }

  return null;
};

export const getNextNegativeOrderStatus = (currentStatus, negativeOrderStatuses = []) => {
  const current = normalizeStatusValue(currentStatus);
  const list = normalizeNegativeOrderStatuses(negativeOrderStatuses);

  for (const parent of list) {
    if (parent.value === current) {
      const subs = [...(parent.subStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      return subs[0]?.value || null;
    }

    const subs = [...(parent.subStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const subIndex = subs.findIndex((item) => item.value === current);
    if (subIndex >= 0 && subIndex < subs.length - 1) {
      return subs[subIndex + 1].value;
    }
  }

  return null;
};

export const getFirstNegativeOrderStatus = (negativeOrderStatuses = []) => {
  const list = [...(negativeOrderStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return list[0]?.value || null;
};

/** Status to restore when leaving a negative flow — prefers `confirmed`, else 2nd positive status */
export const getRecoveryPositiveStatus = (orderStatuses = [], negativeOrderStatuses = []) => {
  const list = getPositiveOrderStatuses(orderStatuses, negativeOrderStatuses);
  const confirmed = list.find((item) => item.value === 'confirmed');
  if (confirmed) return confirmed.value;
  if (list.length >= 2) return list[1].value;
  return list[0]?.value || null;
};

export const getRecoveryPositiveStatusLabel = (orderStatuses = [], negativeOrderStatuses = []) => {
  const value = getRecoveryPositiveStatus(orderStatuses, negativeOrderStatuses);
  if (!value) return '';
  return `Set to ${getStatusMeta(value, orderStatuses, negativeOrderStatuses).label}`;
};

export const isLastNegativeOrderStatus = (currentStatus, negativeOrderStatuses = []) => {
  const list = normalizeNegativeOrderStatuses(negativeOrderStatuses);
  if (!list.length) return normalizeStatusValue(currentStatus) === 'cancelled';

  const current = normalizeStatusValue(currentStatus);
  for (const parent of list) {
    if (parent.value === current) {
      return !(parent.subStatuses || []).length;
    }

    const subs = [...(parent.subStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    if (subs.length && subs[subs.length - 1].value === current) {
      return true;
    }
  }

  return false;
};

export const requiresStatusReason = (status, negativeOrderStatuses = []) => {
  const normalized = normalizeStatusValue(status);
  return normalized === 'cancelled' || normalized === 'returned';
};

export const getStatusReasonField = (status) => {
  const normalized = normalizeStatusValue(status);
  if (normalized === 'cancelled') return 'cancelReason';
  if (normalized === 'returned') return 'returnReason';
  return null;
};

export const getStatusStepperLabel = (nextStatusValue, orderStatuses = [], negativeOrderStatuses = []) => {
  const meta = getStatusMeta(nextStatusValue, orderStatuses, negativeOrderStatuses);
  return `Set to ${meta.label}`;
};

export const isLastOrderStatus = (currentStatus, orderStatuses = [], negativeOrderStatuses = []) => {
  const list = getPositiveOrderStatuses(orderStatuses, negativeOrderStatuses);
  if (!list.length) {
    return normalizeStatusValue(currentStatus) === 'delivered';
  }
  return normalizeStatusValue(currentStatus) === list[list.length - 1].value;
};

export const getStatusSubtext = (currentStatus, orderStatuses = [], negativeOrderStatuses = []) => {
  const meta = getStatusMeta(currentStatus, orderStatuses, negativeOrderStatuses);
  const value = normalizeStatusValue(currentStatus);

  if (isNegativeOrderStatus(value, negativeOrderStatuses)) {
    return `Order has been ${meta.label.toLowerCase()}.`;
  }
  if (isLastOrderStatus(currentStatus, orderStatuses, negativeOrderStatuses)) {
    return `Order has been ${meta.label.toLowerCase()}.`;
  }

  const next = getNextOrderStatus(currentStatus, orderStatuses, negativeOrderStatuses);
  if (next) {
    const nextMeta = getStatusMeta(next, orderStatuses, negativeOrderStatuses);
    return `Ready to update status to ${nextMeta.label.toLowerCase()}.`;
  }

  return `Order is ${meta.label.toLowerCase()}.`;
};

export const formatStatusSubtextForDisplay = (subtext = '') => {
  const text = String(subtext).replace(/\s*you can restore it to [^.]+\./gi, '').trim();
  if (!text) return '';
  if (/^ready to update status to /i.test(text)) return '';
  return text;
};

export const formatPaymentType = (paymentType) => {
  const value = String(paymentType || '').trim().toLowerCase();
  if (!value) return '---';
  if (value === 'cod') return 'COD';
  if (value === 'prepaid') return 'Prepaid';
  if (value === 'advance_paid') return 'Advance Payed';
  return value;
};

export const isAdvancePaidPayment = (paymentType) =>
  String(paymentType || '').trim().toLowerCase() === 'advance_paid';

export const getAdvancePaidAmount = (order) => {
  const amount = Number(order?.advancePaidAmount);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

export const getBalancePaymentAmount = (order) => {
  const total = Number(order?.bill?.totalPrice) || 0;
  const advance = getAdvancePaidAmount(order);
  return Math.max(0, Math.round((total - advance) * 100) / 100);
};

export const buildShipmentFieldRows = (values = {}, { courierMode = false } = {}) => {
  const rows = [];
  const shipmentNumber = values?.[SHIPMENT_FIELD_KEYS.number];
  if (shipmentNumber) {
    rows.push({
      key: SHIPMENT_FIELD_KEYS.number,
      label: 'Shipment Number',
      value: shipmentNumber,
    });
  }
  const shipmentPartner = values?.[SHIPMENT_FIELD_KEYS.partner];
  if (shipmentPartner) {
    rows.push({
      key: SHIPMENT_FIELD_KEYS.partner,
      label: courierMode ? 'Courier Partner' : 'Delivery Boy',
      value: courierMode ? formatShipmentPartner(shipmentPartner) : String(shipmentPartner || '').trim() || '---',
    });
  }
  return rows;
};

export const buildCustomFieldRows = (fieldConfig = [], values = {}, { courierMode = false } = {}) => {
  const rows = (fieldConfig || []).map((field) => ({
    ...field,
    value: values?.[field.key] ?? '',
  }));
  const keys = new Set(rows.map((row) => row.key));

  const shipmentNumber = values?.[SHIPMENT_FIELD_KEYS.number];
  if (shipmentNumber && !keys.has(SHIPMENT_FIELD_KEYS.number)) {
    rows.push({
      key: SHIPMENT_FIELD_KEYS.number,
      label: 'Shipment Number',
      value: shipmentNumber,
    });
  }

  const shipmentPartner = values?.[SHIPMENT_FIELD_KEYS.partner];
  if (shipmentPartner && !keys.has(SHIPMENT_FIELD_KEYS.partner)) {
    rows.push({
      key: SHIPMENT_FIELD_KEYS.partner,
      label: courierMode ? 'Courier Partner' : 'Delivery Boy',
      value: courierMode ? formatShipmentPartner(shipmentPartner) : String(shipmentPartner || '').trim() || '---',
    });
  }

  return rows;
};

const isShipmentField = (field) =>
  /shipment/i.test(field.key || '') || /shipment/i.test(field.label || '');

export const splitCustomFieldsForHeader = (fieldConfig = [], values = {}, options = {}) => {
  const rows = buildCustomFieldRows(fieldConfig, values, options);
  return {
    headerFields: rows.filter(isShipmentField),
    otherFields: rows.filter((field) => !isShipmentField(field)),
  };
};

export const buildStatusFilterOptions = (orderStatuses = [], negativeOrderStatuses = []) => {
  const positive = getPositiveOrderStatuses(orderStatuses, negativeOrderStatuses);
  const negative = normalizeNegativeOrderStatuses(negativeOrderStatuses);
  const options = [
    ...positive.map((item) => ({ value: item.value, label: item.label })),
    ...negative
      .filter((item) => !positive.some((p) => p.value === item.value))
      .map((item) => ({ value: item.value, label: item.label })),
    ...negative.flatMap((item) =>
      (item.subStatuses || [])
        .filter((sub) => !positive.some((p) => p.value === sub.value))
        .map((sub) => ({ value: sub.value, label: sub.label }))
    ),
  ];
  return [{ value: '', label: 'All Statuses' }, ...options];
};

export const getDefaultStatusFilter = (orderStatuses = []) => {
  const list = [...(orderStatuses || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const confirmed = list.find((item) => item.value === 'confirmed');
  return confirmed?.value || list[0]?.value || 'confirmed';
};

const hexToRgb = (color) => {
  const input = String(color || '#2563eb').trim().toLowerCase();
  if (input === 'white') return { r: 255, g: 255, b: 255 };
  if (input === 'black') return { r: 0, g: 0, b: 0 };

  let hex = input.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map((char) => char + char).join('');
  }
  if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
    return { r: 37, g: 99, b: 235 };
  }

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

const getLuminance = (r, g, b) => {
  const channels = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

/** Returns white or dark text for readable contrast on a solid background */
export const getContrastTextColor = (color = '#2563eb') => {
  const { r, g, b } = hexToRgb(color);
  return getLuminance(r, g, b) > 0.55 ? '#111827' : '#ffffff';
};

export const getStatusBadgeStyles = (color = '#2563eb') => {
  const { r, g, b } = hexToRgb(color);
  const luminance = getLuminance(r, g, b);
  const isLight = luminance > 0.55;
  const isVeryLight = luminance > 0.85;

  return {
    backgroundColor: isVeryLight ? '#f3f4f6' : `rgba(${r}, ${g}, ${b}, 0.12)`,
    color: isLight ? '#111827' : (color || '#2563eb'),
    ...(isVeryLight ? { border: '1px solid #e5e7eb' } : {}),
  };
};

/** Solid status button — picks light or dark label based on background */
export const getStatusButtonStyles = (color = '#2563eb') => ({
  backgroundColor: color || '#2563eb',
  color: getContrastTextColor(color),
});

export const DEFAULT_ORDER_LIST_COLUMNS = [
  { key: 'status', label: 'Status', visible: true, position: 0 },
  { key: 'orderId', label: 'Order ID', visible: true, position: 1 },
  { key: 'customer', label: 'Customer', visible: true, position: 2 },
  { key: 'zone', label: 'Zone / Pincode', visible: true, position: 3 },
  { key: 'pincode', label: 'Pincode', visible: false, position: 25 },
  { key: 'shipmentId', label: 'Shipment Id', visible: false, position: 4 },
  { key: 'courier', label: 'Delivery Boy', visible: true, position: 5 },
  { key: 'driver', label: 'Driver', visible: true, position: 6 },
  { key: 'total', label: 'Total Amount', visible: true, position: 7 },
  { key: 'deliveryCharge', label: 'Delivery Charge', visible: false, position: 27 },
  { key: 'discount', label: 'Discount', visible: false, position: 28 },
  { key: 'admin', label: 'Admin', visible: true, position: 29 },
  { key: 'created', label: 'Created', visible: true, position: 8 },
  { key: 'delivery', label: 'Delivery', visible: true, position: 9 },
  { key: 'orderButtons', label: 'Order Buttons', visible: true, position: 10 },
  {key: 'customerPhone', label: 'Phone Number', visible: false, position: 11},
  {key: 'address', label: 'Address', visible: false, position: 12},
  {key: 'buildingNumber', label: 'Building Number', visible: false, position: 13},
  {key: 'apartment', label: 'Apartment', visible: false, position: 14},
  {key: 'directionToReach', label: 'Direction to Reach', visible: false, position: 15},
  {key: 'state', label: 'State', visible: false, position: 16},
  {key: 'district', label: 'District', visible: false, position: 17},
  {key: 'country', label: 'Country', visible: false, position: 18},
  {key: 'city', label: 'City', visible: false, position: 19},
  {key: 'paymentType', label: 'Payment Type', visible: false, position: 20},
  {key: 'itemTotal', label: 'Item Total', visible: false, position: 21},
  {key: 'productCount', label: 'Product Count', visible: false, position: 22},
  {key: 'orderNote', label: 'Order Note', visible: false, position: 23},
  {key: 'returnReason', label: 'Return Reason', visible: false, position: 24},
  {key: 'comments', label: 'Comment', visible: false, position: 26},
];

const LEGACY_COLUMN_KEY_MAP = {
  totalPrice: 'total',
  createdAt: 'created',
  pincode: 'zone',
};

const mapLegacyColumnKey = (key) => LEGACY_COLUMN_KEY_MAP[key] || key;

export const normalizeOrderListColumns = (columns = []) => {
  const input = Array.isArray(columns) ? columns : [];

  if (input.length > 0 && typeof input[0] === 'string') {
    return input.map((rawKey, index) => {
      const key = mapLegacyColumnKey(String(rawKey || '').trim());
      const defaultCol = DEFAULT_ORDER_LIST_COLUMNS.find((col) => col.key === key);
      return {
        key,
        label: defaultCol?.label || key,
        visible: true,
        position: index,
      };
    });
  }

  const defaults = DEFAULT_ORDER_LIST_COLUMNS.map((item) => ({ ...item }));
  const byKey = new Map(input.map((item) => [String(item?.key || '').trim(), item]));

  const merged = defaults.map((defaultCol, index) => {
    const saved = byKey.get(defaultCol.key);
    if (!saved) return { ...defaultCol, position: index };
    return {
      key: defaultCol.key,
      label: String(saved.label || defaultCol.label).trim() || defaultCol.label,
      visible: saved.visible !== false,
      position: Number.isFinite(saved.position) ? saved.position : index,
    };
  });

  input.forEach((item) => {
    const key = String(item?.key || '').trim();
    if (!key || merged.some((col) => col.key === key)) return;
    merged.push({
      key,
      label: String(item.label || key).trim() || key,
      visible: item.visible !== false,
      position: Number.isFinite(item.position) ? item.position : merged.length,
    });
  });

  return merged
    .sort((a, b) => a.position - b.position)
    .map((item, index) => ({ ...item, position: index }));
};

export const getVisibleOrderListColumns = (columns = []) =>
  normalizeOrderListColumns(columns).filter((col) => col.visible);

const INDIA_ONLY_ORDER_LIST_COLUMN_KEYS = new Set(['pincode', 'state', 'city', 'country']);
const HIDDEN_ORDER_LIST_COLUMN_KEYS = new Set([
  'courier',
  'shipmentId',
  'shipment_id',
  'shipment_number',
  'shipment_partner',
]);

export const isOrderListColumnAllowedForRegion = (key, isIndia = false) => {
  const columnKey = String(key || '').trim();
  if (!columnKey) return false;
  if (columnKey === 'district') return false;
  if (isIndia) {
    if (HIDDEN_ORDER_LIST_COLUMN_KEYS.has(columnKey)) return false;
    return columnKey !== 'zone';
  }
  return !INDIA_ONLY_ORDER_LIST_COLUMN_KEYS.has(columnKey);
};

export const filterOrderListColumnsForRegion = (columns = [], isIndia = false) =>
  (columns || []).filter((col) => isOrderListColumnAllowedForRegion(col.key, isIndia));

export const migrateOrderListColumnsForRegion = (columns = [], isIndia = false) => {
  let list = [...(columns || [])];

  if (isIndia) {
    if (!list.some((col) => col.key === 'pincode')) {
      const zoneCol = list.find((col) => col.key === 'zone');
      list.push({
        key: 'pincode',
        label: 'Pincode',
        visible: !!zoneCol?.visible,
        position: list.length,
      });
    }

    const zoneCol = list.find((col) => col.key === 'zone');
    const pincodeCol = list.find((col) => col.key === 'pincode');
    if (zoneCol?.visible && pincodeCol) {
      pincodeCol.visible = true;
      zoneCol.visible = false;
    }
  }

  return filterOrderListColumnsForRegion(list, isIndia);
};

export const getOrderListColumnLabel = (column, isIndia = false) => {
  if (column.key === 'zone') return isIndia ? 'Pincode' : 'Zone Number';
  if (column.key === 'orderButtons') return 'Order Buttons';
  return column.label;
};
